import React, { type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { SelectOption } from '@/components/ui/select';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { parseObjectToSearchParams } from '@/utils/parseSearchParams';

import type { EstatesFiltersSchemaType } from '../schemas';
import { DEFAULT_ESTATE_FILTERS_VALUES } from '../schemas/estates-filters';
import { EstateFilterNewUi } from './estate-filter-new-ui';

export interface EstateFilterProps {
  creatorList?: SelectOption[];
  searchParams: {
    enableFilters?: boolean;
    filters?: EstatesFiltersSchemaType;
    filterOwn?: boolean;
    view?: 'map' | 'grid' | 'list';
  };
  viewOptions?: {
    /** Hides the area and rooms ranges, used by the compact Paper 2 Pixel estate picker */
    hideLivingAreaAndRooms?: boolean;
  };
  onUpdateSearchParams: (filters: EstatesFiltersSchemaType) => void;
  /** Shows the "Clear All Filters" button when true */
  hasFilters?: boolean;
  /** Called by the "Clear All Filters" button */
  onClear?: () => void;
}

export const EstateFilter: FC<EstateFilterProps> = ({
  creatorList,
  searchParams,
  viewOptions,
  onUpdateSearchParams,
  hasFilters = false,
  onClear,
}) => {
  const { filters, filterOwn } = searchParams;

  const methods = useForm<EstatesFiltersSchemaType>({
    defaultValues: {
      ...DEFAULT_ESTATE_FILTERS_VALUES,
      creatorId: filterOwn && creatorList?.length ? creatorList[0].id : undefined,
    },
  });

  const { reset, watch } = methods;

  const updateSearchParams = (params: EstatesFiltersSchemaType) => {
    const filters = parseObjectToSearchParams<EstatesFiltersSchemaType>(params);
    onUpdateSearchParams(filters);
  };

  const debouncedUpdateSearchParamFn = useDebouncedCallback(updateSearchParams, 1000);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useEffect(() => {
    reset((prev) => ({ ...prev, ...filters }));
    const sub = watch((data) => {
      debouncedUpdateSearchParamFn(data);
    });

    return () => sub.unsubscribe();
  }, []);

  return (
    <FormProvider {...methods}>
      <EstateFilterNewUi
        creatorList={creatorList}
        filterOwn={filterOwn}
        hideLivingAreaAndRooms={viewOptions?.hideLivingAreaAndRooms}
        hasFilters={hasFilters}
        onClear={onClear}
      />
    </FormProvider>
  );
};
